import { useMemo } from 'react'

import { type MessageDescriptor } from 'react-intl'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'

import {
  makeSelectEntitlementAccessConfig,
  makeSelectFeatureFlags,
  makeSelectFeatureSwitches,
  makeSelectPermissions,
  makeSelectRole,
} from '@webapp/core/stores/global'
import { PermissionGroupNames } from '@webapp/platform/_unmigrated/models/PermissionGroups'
import {
  type ExportDataTypeConfig,
  type ExportDataTypeKey,
  type ExportDataTypeVisibilityGates,
  exportDataTypeConfigs,
  isExportDataTypeVisible,
} from '@webapp/platform/import-export/entities/ExportDataTypeConfig'
import messages from '../messages'
import { type RequestedExportsPageParams } from '../types'

// Per-type breadcrumb labels keyed by config.key (Angular breadcrumb
// parity: Settings → Export data as CSV → <type title>)
const TYPE_LABELS: Record<ExportDataTypeKey, MessageDescriptor> = {
  order_items: messages.orderItemsLabel,
  po_items: messages.poItemsLabel,
  expense_report_history: messages.expenseReportHistoryLabel,
  travel_history: messages.travelHistoryLabel,
  budget: messages.budgetLabel,
  receiving_history: messages.receivingHistoryLabel,
  catalog: messages.catalogLabel,
  bills: messages.billsLabel,
  users: messages.usersLabel,
  locations: messages.locationsLabel,
  receive_log: messages.receiveLogLabel,
  unbilled_po_items: messages.unbilledPoItemsLabel,
  audit_importlog: messages.auditImportLogLabel,
  admin_auditlog: messages.adminAuditLogLabel,
  superuser_auditlog: messages.superuserAuditLogLabel,
  spending_card_transactions: messages.spendingCardTransactionsLabel,
  budgets: messages.budgetsRedesignedLabel,
  spend_data: messages.spendDataRedesignedLabel,
  contracts: messages.contractsLabel,
}

export interface UseRequestedExportsPageGuardResult {
  /** false while denied — the Page redirects to the landing page (BHV-036) */
  isAllowed: boolean
  exportConfig: Nullable<ExportDataTypeConfig>
  /** Breadcrumb label for the resolved type (null when denied) */
  typeLabel: Nullable<MessageDescriptor>
}

/**
 * Page-level route guard (BHV-036, Angular `redirectTo` parity): resolves
 * :exportType to its config and re-runs the landing-row visibility gate
 * against a global-store snapshot. Unknown key or gate failure → the Page
 * redirects to the landing page with `accessDenied` location state; the
 * landing page fires the warning toast (AUTHORIZATION.ACCESS_ERROR_*
 * equivalents) once mounted. Toasting from this guard is unreliable on
 * hard page loads — the guard runs during app bootstrap before the
 * ToastContainer subscribes, so the toast is silently dropped. Guard lives
 * at Page level — NOT in features or the Feature Set.
 */
export const useRequestedExportsPageGuard =
  (): UseRequestedExportsPageGuardResult => {
    const { exportType } = useParams<RequestedExportsPageParams>()

    const entitlements = useSelector(makeSelectEntitlementAccessConfig())
    const permissions = useSelector(makeSelectPermissions())
    const role = useSelector(makeSelectRole())
    // Config gate keys are plain strings — index the typed records
    // dynamically (list-exportable-data-types Model precedent)
    const featureSwitches = useSelector(
      makeSelectFeatureSwitches()
    ) as unknown as Record<string, boolean | undefined>
    const featureFlags = useSelector(
      makeSelectFeatureFlags()
    ) as unknown as Record<string, boolean | undefined>

    const exportConfig: ExportDataTypeConfig | undefined =
      exportDataTypeConfigs[exportType as ExportDataTypeKey]

    const isAllowed = useMemo(() => {
      if (!exportConfig) {
        return false
      }

      const gates: ExportDataTypeVisibilityGates = {
        isSuperuser:
          role?.name === PermissionGroupNames.SUPERUSER ||
          role?.name === PermissionGroupNames.SITE_ADMINISTRATOR,
        granularPermissions: featureSwitches.granular_permissions === true,
        permissions: (permissions ?? []).map(
          (permission) => permission.codename
        ),
        entitlements,
        featureFlags,
        featureSwitches,
      }

      return isExportDataTypeVisible(exportConfig, gates)
    }, [
      exportConfig,
      entitlements,
      permissions,
      role,
      featureSwitches,
      featureFlags,
    ])

    if (!isAllowed || !exportConfig) {
      return { isAllowed: false, exportConfig: null, typeLabel: null }
    }

    const key = exportConfig.key as ExportDataTypeKey
    const typeLabel = TYPE_LABELS[key]

    return { isAllowed: true, exportConfig, typeLabel }
  }
